/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Link, Typography, Typography2, useTheme } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

import NewSkillSetCard from './NewSkillSetCard';
import type { SkillSetSearchResults } from './SkillSetSearchDataProvider';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

type Props = {
  skillsetHits: SkillSetSearchResults[];
  programHomeLink: string;
};

const useStyles = () => {
  const { spacing, breakpoints } = useTheme();
  return {
    banner: css`
      ${breakpoints.down('sm')} {
        margin: ${spacing(12)};
      }
    `,
    header: css`
      display: flex;
      gap: ${spacing(12)};
      padding: ${spacing(0, 0, 8)};
      flex-wrap: wrap;
      align-items: end;
    `,
    // To fix alignment issues
    allSkillSetsLink: css`
      height: 24px;
    `,
    skillsetsContainer: css`
      padding-top: ${spacing(12)};
      padding-bottom: ${spacing(24)};
      display: flex;
      flex-wrap: wrap;
      gap: ${spacing(24)};
    `,
    skillsetCard: css`
      flex: 1 1 33%;
      max-width: 31%;
      ${breakpoints.down('sm')} {
        flex: 1 1 100%;
        max-width: 100%;
      }
    `,
  };
};

const NewSkillSetSearchResultBanner = ({ skillsetHits, programHomeLink }: Props) => {
  const styles = useStyles();

  const matches = skillsetHits[0].hits.slice(0, 3);

  const trackingData = {
    skillMatches: matches.map((match) => ({
      name: match.name,
      objectID: match.objectID,
    })),
  };

  return (
    <div css={styles.banner}>
      <div css={styles.header}>
        <Typography variant="h2semibold" component="h2">
          {_t('Find your next course with a SkillSet role')}
        </Typography>
        <TrackedLink
          {...Link.defaultProps}
          css={styles.allSkillSetsLink}
          trackingName="search_banner_all_skillsets"
          trackingData={trackingData}
          href={`${programHomeLink}?currentTab=SKILLSETS`}
        >
          {_t('Explore all available SkillSets')}
        </TrackedLink>
      </div>
      <Typography2 component="p">
        {_t('Track your progress and get course suggestions tailored to your skill level')}
      </Typography2>
      <div css={styles.skillsetsContainer}>
        {matches.map((skillset, index) => (
          <NewSkillSetCard
            key={skillset.objectID}
            css={styles.skillsetCard}
            skillsetName={skillset.name}
            url={`${programHomeLink}/skillsets/${skillset.objectID.split('/')[1]}`}
            trackingData={{
              matches,
              skillset,
              index,
            }}
            source="search_banner_skillset_card"
          />
        ))}
      </div>
    </div>
  );
};

export default NewSkillSetSearchResultBanner;
